import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SITE_NAME = 'Rastyle Republik';
const DEFAULT_DESCRIPTION = 'The global reggae community. Music, events, culture. One love, one platform.';
const DEFAULT_IMAGE = '/og-image.jpg';
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://www.rastyle.co.ke').replace(/\/+$/, '');

const absoluteUrl = (value, fallback = SITE_URL) => {
  if (!value) return fallback;
  try {
    return new URL(value, `${SITE_URL}/`).toString();
  } catch {
    return fallback;
  }
};

const withSiteName = (title, siteName) => {
  const clean = String(title || '').trim().replace(/Rastyle\s+Kenya/gi, SITE_NAME);
  if (!clean) return siteName;
  return clean.toLowerCase().includes(siteName.toLowerCase())
    ? clean
    : `${clean} | ${siteName}`;
};

// Cache admin OG meta globally so we don't refetch on every page
let _ogMetaCache = null;
let _ogMetaFetched = false;

const fetchOgMeta = async () => {
  if (_ogMetaFetched) return _ogMetaCache;
  try {
    const res = await axios.get(`${API}/api/og-meta/public`);
    if (res.data && res.data.og_title) {
      _ogMetaCache = res.data;
    }
  } catch {}
  _ogMetaFetched = true;
  return _ogMetaCache;
};

// Inject JSON-LD into the document head reliably
const useJsonLd = (data) => {
  useEffect(() => {
    if (!data) return;
    const id = 'rastyle-jsonld';
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [data]);
};

const useManagedSeoHead = ({
  description,
  image,
  imageAlt,
  imageHeight,
  imageWidth,
  locale,
  noindex,
  siteName,
  title,
  twitterCard,
  twitterSite,
  type,
  url,
}) => {
  useEffect(() => {
    const definitions = [
      { tag: 'link', selector: 'link[rel="canonical"]', attributes: { rel: 'canonical', href: url } },
      { tag: 'meta', selector: 'meta[name="description"]', attributes: { name: 'description', content: description } },
      { tag: 'meta', selector: 'meta[property="og:type"]', attributes: { property: 'og:type', content: type } },
      { tag: 'meta', selector: 'meta[property="og:title"]', attributes: { property: 'og:title', content: title } },
      { tag: 'meta', selector: 'meta[property="og:description"]', attributes: { property: 'og:description', content: description } },
      { tag: 'meta', selector: 'meta[property="og:image"]', attributes: { property: 'og:image', content: image } },
      { tag: 'meta', selector: 'meta[property="og:image:alt"]', attributes: { property: 'og:image:alt', content: imageAlt } },
      { tag: 'meta', selector: 'meta[property="og:image:width"]', attributes: { property: 'og:image:width', content: String(imageWidth) } },
      { tag: 'meta', selector: 'meta[property="og:image:height"]', attributes: { property: 'og:image:height', content: String(imageHeight) } },
      { tag: 'meta', selector: 'meta[property="og:locale"]', attributes: { property: 'og:locale', content: locale } },
      { tag: 'meta', selector: 'meta[property="og:url"]', attributes: { property: 'og:url', content: url } },
      { tag: 'meta', selector: 'meta[property="og:site_name"]', attributes: { property: 'og:site_name', content: siteName } },
      { tag: 'meta', selector: 'meta[name="twitter:card"]', attributes: { name: 'twitter:card', content: twitterCard } },
      { tag: 'meta', selector: 'meta[name="twitter:title"]', attributes: { name: 'twitter:title', content: title } },
      { tag: 'meta', selector: 'meta[name="twitter:description"]', attributes: { name: 'twitter:description', content: description } },
      { tag: 'meta', selector: 'meta[name="twitter:image"]', attributes: { name: 'twitter:image', content: image } },
      { tag: 'meta', selector: 'meta[name="twitter:image:alt"]', attributes: { name: 'twitter:image:alt', content: imageAlt } },
      ...(twitterSite ? [{
        tag: 'meta',
        selector: 'meta[name="twitter:site"]',
        attributes: { name: 'twitter:site', content: twitterSite },
      }] : []),
      ...(noindex ? [{
        tag: 'meta',
        selector: 'meta[name="robots"]',
        attributes: { name: 'robots', content: 'noindex,nofollow' },
      }] : []),
    ];

    const mutations = definitions.map(({ tag, selector, attributes }) => {
      let element = document.head.querySelector(selector);
      const created = !element;
      if (!element) {
        element = document.createElement(tag);
        document.head.appendChild(element);
      }
      const previous = Object.fromEntries(
        Object.keys(attributes).map((attribute) => [attribute, element.getAttribute(attribute)]),
      );
      Object.entries(attributes).forEach(([attribute, value]) => {
        element.setAttribute(attribute, value);
      });
      return { attributes, created, element, previous };
    });

    return () => {
      mutations.forEach(({ attributes, created, element, previous }) => {
        if (created) {
          element.remove();
          return;
        }
        Object.keys(attributes).forEach((attribute) => {
          const value = previous[attribute];
          if (value === null) element.removeAttribute(attribute);
          else element.setAttribute(attribute, value);
        });
      });
    };
  }, [
    description,
    image,
    imageAlt,
    imageHeight,
    imageWidth,
    locale,
    noindex,
    siteName,
    title,
    twitterCard,
    twitterSite,
    type,
    url,
  ]);
};

const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  canonicalPath,
  canonical,
  type = 'website',
  noindex = false,
  jsonLd,
  event,
  creator,
  imageAlt,
  imageWidth = 1200,
  imageHeight = 630,
  locale,
}) => {
  const [adminMeta, setAdminMeta] = useState(_ogMetaCache);

  useEffect(() => {
    if (!_ogMetaFetched) {
      fetchOgMeta().then(meta => { if (meta) setAdminMeta(meta); });
    }
  }, []);

  // Admin-configured meta takes effect as defaults (page-specific props override)
  const siteName = /Rastyle\s+Kenya/i.test(adminMeta?.og_site_name || '')
    ? SITE_NAME
    : (adminMeta?.og_site_name || SITE_NAME);
  const pageTitle = withSiteName(title || adminMeta?.og_title, siteName);
  const pageDescription = description || adminMeta?.og_description || DEFAULT_DESCRIPTION;
  const pageImage = absoluteUrl(image || adminMeta?.og_cover || adminMeta?.og_logo || DEFAULT_IMAGE);
  const canonicalValue = canonical || canonicalPath || url || '/';
  const pageUrl = absoluteUrl(canonicalValue);
  const pageImageAlt = imageAlt || adminMeta?.og_image_alt || `${pageTitle} social preview`;
  const pageLocale = locale || adminMeta?.og_locale || 'en_KE';
  const twitterCard = adminMeta?.twitter_card || 'summary_large_image';

  // Build JSON-LD from event/creator/custom
  let structuredData = jsonLd || null;

  if (!structuredData && event) {
    structuredData = {
      '@context': 'https://schema.org',
      '@type': 'MusicEvent',
      name: event.name,
      startDate: event.date,
      endDate: event.end_date,
      location: {
        '@type': 'Place',
        name: event.venue,
        address: event.venue_address || event.city
      },
      description: event.description,
      image: event.image,
      offers: event.price ? {
        '@type': 'Offer',
        price: event.price,
        priceCurrency: event.currency || 'KES',
        availability: 'https://schema.org/InStock',
        url: pageUrl
      } : undefined,
      organizer: event.organizer ? {
        '@type': 'Organization',
        name: event.organizer
      } : undefined
    };
  }

  if (!structuredData && creator) {
    structuredData = {
      '@context': 'https://schema.org',
      '@type': 'MusicGroup',
      name: creator.display_name,
      description: creator.bio,
      image: creator.profile_image,
      url: pageUrl,
      genre: 'Reggae',
      sameAs: [
        creator.socials?.instagram && `https://instagram.com/${creator.socials.instagram}`,
        creator.socials?.twitter && `https://twitter.com/${creator.socials.twitter}`,
        creator.socials?.youtube_channel,
        creator.socials?.spotify_artist_id && `https://open.spotify.com/artist/${creator.socials.spotify_artist_id}`
      ].filter(Boolean)
    };
  }

  useJsonLd(structuredData);
  useManagedSeoHead({
    description: pageDescription,
    image: pageImage,
    imageAlt: pageImageAlt,
    imageHeight: adminMeta?.og_image_height || imageHeight,
    imageWidth: adminMeta?.og_image_width || imageWidth,
    locale: pageLocale,
    noindex,
    siteName,
    title: pageTitle,
    twitterCard,
    twitterSite: adminMeta?.twitter_site,
    type,
    url: pageUrl,
  });

  return (
    <Helmet>
      <title>{pageTitle}</title>
    </Helmet>
  );
};

export default SEO;
